<template>
  <div>
    <Title
      class="title"
      text="数据概览"
    />

    <!-- 大区 -->
    <Cascader
      v-model="regionDefault"
      class="comp"
      style="width: 200px;"
      clearable
      :options="region"
      @change="handleCascaderChange"
      @expand-change="handleCascaderExpandChange"
    />

    <!-- 时间 -->
    <Select
      v-model="date"
      class="comp"
      @change="handleDateSelectChange"
    >
      <Option
        v-for="item in dateOptions"
        :key="item.value"
        :label="item.label"
        :value="item.value"
      />
    </Select>

    <!-- 日期 -->
    <DatePicker
      :default-date="[startDate, endDate]"
      @change="handleDatePickerChange"
      @checkbox-change="handleDatePickerCheckboxChange"
    />

    <Button
      type="primary"
      plain
      style="margin-left: 10px"
      @click="refresh"
    >
      刷新数据
    </Button>

    <!-- 图表数据 -->
    <Tabs
      v-model="tabType"
      type="border-card"
      class="tabs"
      @tab-click="handleTabsChange"
    >
      <TabPane
        label="趋势分析"
        name="trend"
      >
        <TrendAnalysis
          :dataSource="trendAnalysisDataSource"
          :defaultTarget="trendAnalysisDefaultTargets[0]"
          :showContrastFeature="showTrendAnalysisContrast"
          @change="handleTrendAnalysisChange"
        />
      </TabPane>

      <TabPane
        label="占比分析"
        name="proportion"
      >
        <ProportionAnalysis
          :dataSource="proportionAnalysisDataSource"
          :defaultDimension="proportionAnalysisDimension"
          :defaultTarget="proportionAnalysisTarget"
          @change="handleProportionAnalysisChange"
        />
      </TabPane>
    </Tabs>

    <div class="detail-actions">
      <Title text="详细数据" />
      <div>
        <Button @click="handleAggregateType">聚合类型</Button>
        <Button @click="handleCustomColumn">自定义列</Button>
        <DownloadReport
          style="margin-left: 10px"
          topic="channel_sale"
          :args="dowloadArgs"
        />
      </div>
    </div>

    <Table
      show-summary
      :summary-method="handleSummaryMethod"
      :data="dataSource"
      @sort-change="handleSortChange"
    >
      <template v-for="c1 in columns">
        <TableColumn
          v-if="checkIsShow(c1.isShow)"
          :key="c1.key"
          :label="c1.name"
          :min-width="c1.minWidth || 150"
          :prop="c1.key"
          :sortable="c1.sortable ? 'custom' : false"
        >
          <template v-for="c2 in c1.children">
            <TableColumn
              v-if="checkIsShow(c2.isShow)"
              :key="c2.key"
              :label="c2.name"
              :min-width="c2.minWidth || 150"
              :prop="c2.key"
              :sortable="c2.sortable ? 'custom' : false"
            >
              <template
                v-if="c2.filterable"
                slot="header"
              >
                <span>{{ c2.name }}</span>
                <div class="table-filter">
                  <TableFilter
                    :type="c2.key"
                    @confirm="handleTableFilterConfirm"
                    @reset="handleTableFilterReset"
                  />
                </div>
              </template>
            </TableColumn>
          </template>
        </TableColumn>
      </template>
    </Table>

    <Pagination
      class="pagination"
      :current-page="page"
      :page-sizes="[1, 10, 20, 30, 50, 100]"
      :page-size="page_size"
      layout="total, sizes, prev, pager, next"
      :total="total"
      @size-change="handlePaginationSizeChange"
      @current-change="handlePaginationCurrentChange"
    />

    <CustomColumn
      :defaultData="fieldColumns"
      :visible.sync="customColumnVisible"
      @confirm="handleCustomColumnConfirm"
    />

    <AggregateType
      :defaultData="dimensionColumns"
      :disabledData="dimensionDisabled"
      :visible.sync="aggregateTypeVisible"
      @confirm="handleAggregateTypeConfirm"
    />
  </div>
</template>

<script>
import {
  Table,
  TableColumn,
  Button,
  Pagination,
  Cascader,
  Select,
  Option,
  Tabs,
  TabPane,
} from 'element-ui';
import { uniqBy } from 'lodash';
import moment from 'moment';

import Title from '@/components/Title';
import CustomColumn from './components/CustomColumn';
import AggregateType from './components/AggregateType';
import TableFilter from './components/TableFilter';
import DatePicker from './components/DatePicker';
import TrendAnalysis from './TrendAnalysis';
import ProportionAnalysis from './ProportionAnalysis';
import DownloadReport from '@/components/DownloadReport/Index';

import * as $cache from '@/common/cache';
import { arrToTree } from './util';
import {
  defaultFieldsList,
  targetFieldsList,
  dimensionFieldsList,
} from './config/';
import { compose } from './config/group';
import dateOptions from './config/date';

import requestMixins from './mixin/request';
import paramsMixins from './mixin/params';

// 用户设置的自定义列信息
const CUSTOM_TARGET_FIELDS = 'CUSTOM_TARGET_FIELDS';
// 用户设置的聚合类型信息
const CUSTOM_DIMENSION_FIELDS = 'CUSTOM_DIMENSION_FIELDS';

export default {
  components: {
    Table,
    TableColumn,
    Button,
    Pagination,
    Cascader,
    Select,
    Option,
    Tabs,
    TabPane,

    CustomColumn,
    AggregateType,
    TableFilter,
    DatePicker,
    TrendAnalysis,
    ProportionAnalysis,
    Title,
    DownloadReport,
  },
  mixins: [requestMixins, paramsMixins],

  data() {
    return {
      /** *** 大区 *****/
      regionDefault: [''],
      // 大区信息列表
      region: [],
      // 地区id
      regionId: '',
      // 销售id
      saleId: '',

      // 时间维度对应的值
      date: 'day',

      // 是否显示趋势分析的对比指标
      // 时间对比和指标对比同时只能开启一个
      showTrendAnalysisContrast: true,

      // 自定义列
      field: this.getLocalTargets().keys || this.getDefaultFields().keys,
      // 动态表单字段
      fieldColumns:
        this.getLocalTargets().columns || this.getDefaultFields().columns,
      // 维度字段
      dimension:
        this.getLocalDimension().keys || this.getDefaultDimension().keys,
      // 维度字段 禁用项
      dimensionDisabled: [],
      // 维度对应的表格列
      dimensionColumns:
        this.getLocalDimension().columns || this.getDefaultDimension().columns,
      customColumnVisible: false,
      aggregateTypeVisible: false,
      page: 1,
      page_size: 10,
      // 排序方法
      order: 'cost_total:desc',
      dateOptions,
      dataSource: [],
      // 合计数据
      dataSourceSum: [],
      // 过滤字段
      filter: {},
      // 数据总数
      total: 0,
      // 起始时间
      startDate: moment()
        .subtract(30, 'day')
        .format('YYYY-MM-DD'),
      // 结束时间
      endDate: moment()
        .subtract(1, 'day')
        .format('YYYY-MM-DD'),
      // 比较用起始时间
      compStartDate: '',
      // 比较用结束时间
      compEndDate: '',

      /** *** 图表相关 *****/
      // 当前展示的分析图表类型
      tabType: 'trend',
      // 趋势分析数据
      trendAnalysisDataSource: {},
      // 趋势分析默认指标
      trendAnalysisDefaultTargets: ['cost_total'],
      // 占比分析数据
      proportionAnalysisDataSource: null,
      // 占比分析默认维度
      proportionAnalysisDimension: 'region',
      // 占比分析默认指标
      proportionAnalysisTarget: 'cost_total',
    };
  },

  computed: {
    columns() {
      let temp = this.fieldColumns.concat(this.dimensionColumns);
      temp = uniqBy(temp, 'key');
      const col = compose(arrToTree(temp));

      // 改变顺序
      col.forEach((item, index) => {
        if (item.key === 'base_info') {
          col.splice(index, 1);
          col.splice(0, 0, item);
        }
      });

      return col;
    },

    dowloadArgs() {
      const params = this.getListParams();
      return params;
    },
  },

  created() {
    this.getList();
    this.getRegions();
    this.getTrendAnalysisData();

    // 初始一次设置列；
    this.handleCustomColumnConfirm(
      {
        keys: this.field,
        columns: this.fieldColumns,
      },
      true,
    );
  },

  methods: {
    // 刷新数据
    refresh() {
      this.page = 1;
      this.getList();
      if (this.tabType === 'trend') {
        this.getTrendAnalysisData();
      } else if (this.tabType === 'proportion') {
        this.getProportionAnalysisData();
      }
    },

    // 切换分析图表
    handleTabsChange() {
      if (this.tabType === 'proportion' && !this.proportionAnalysisDataSource) {
        this.getProportionAnalysisData();
      }
    },

    // 趋势分析图调整指标回调
    handleTrendAnalysisChange(data) {
      if (data.type === 'base') {
        this.trendAnalysisDefaultTargets[0] = data.key;
      } else if (data.type === 'contrast') {
        if (data.key) {
          this.trendAnalysisDefaultTargets[1] = data.key;
        } else {
          this.trendAnalysisDefaultTargets.splice(1, 1);
        }
      }
      this.getTrendAnalysisData();
    },

    // 占比分析图调整维度或者指标
    handleProportionAnalysisChange(data) {
      if (data.type === 'dimension') {
        this.proportionAnalysisDimension = data.value;
      } else if (data.type === 'target') {
        this.proportionAnalysisTarget = data.value;
      }
      this.getProportionAnalysisData();
    },

    // 时间切换
    handleDatePickerChange(data) {
      data.forEach((item) => {
        if (item.type === 'base') {
          this.startDate = item.start;
          this.endDate = item.end;
        } else if (item.type === 'contrast') {
          this.compStartDate = item.start;
          this.compEndDate = item.end;
        }
      });

      this.refresh();
    },

    // 时间对比切换
    handleDatePickerCheckboxChange(data) {
      if (data.type === 'contrast') {
        if (data.start) {
          this.compStartDate = data.start;
        } else {
          this.compStartDate = '';
        }

        if (data.end) {
          this.compEndDate = data.end;
        } else {
          this.compEndDate = '';
        }
      }

      // 开启时间对比则关闭趋势分析的指标对比
      this.showTrendAnalysisContrast = !data.start;

      this.refresh();
    },

    // 修改时间
    handleDateSelectChange() {
      this.refresh();
    },

    // 修改大区
    handleCascaderChange(value) {
      this.regionId = value[0];
      this.saleId = value[1];
      this.refresh();
    },

    // 修改大区下的销售
    handleCascaderExpandChange(value) {
      this.regionId = value[0];
      this.refresh();
    },

    handlePaginationSizeChange(size) {
      this.page_size = size;
      this.getList();
    },

    handlePaginationCurrentChange(current) {
      this.page = current;
      this.getList();
    },

    // 显示聚合类型对话框
    handleAggregateType() {
      this.aggregateTypeVisible = true;
    },

    // 显示自定义列对话框
    handleCustomColumn() {
      this.customColumnVisible = true;
    },

    // 设置聚合类型
    handleAggregateTypeConfirm({ keys, columns }) {
      $cache.localSet(CUSTOM_DIMENSION_FIELDS, keys);
      this.dimension = keys;
      this.dimensionColumns = columns;
      this.getList();
    },

    _hasResourceField(fields = []) {
      return ['online_resources_num', 'new_resources_num'].some((i) =>
        fields.includes(i),
      );
    },

    _onlyResourceField(fields = []) {
      return (
        ((fields.includes('online_resources_num') ||
          fields.includes('new_resources_num')) &&
          fields.length === 1) ||
        (fields.includes('online_resources_num') &&
          fields.includes('new_resources_num') &&
          fields.length === 2)
      );
    },

    _filterDimensionByDisabledFields(fields = []) {
      this.dimension = this.dimension.filter((i) => {
        return !fields.includes(i);
      });
      this.dimensionColumns = this.dimensionColumns.filter((i) => {
        return !fields.includes(i.trunk) && !fields.includes(i.key);
      });
    },

    // 设置自定义列
    handleCustomColumnConfirm({ keys, columns }, init = false) {
      /**
       * 聚合类型选项重置；
       *
       * [自定义列] 包含 [在线素材数/新增素材数]
       * [聚合维度] [媒体类型/栏位类型/创意类型/是否开启OCPC] 不可选, 剩余可选
       *
       * [自定义列] 仅包含 [在线素材数/新增素材数]
       * [聚合维度] [素材类型] 可选
       *
       * [自定义列] 不包含 [在线素材数/新增素材数]
       * [聚合维度] [素材类型] 不可选, 剩余可选
       */

      if (this._hasResourceField(keys)) {
        this.dimensionDisabled = [
          'media_type',
          'adslot_type',
          'idea_type',
          'is_ocpc',
        ];

        if (!this._onlyResourceField(keys)) {
          this.dimensionDisabled.push('resource_type');
        }
      } else {
        this.dimensionDisabled = ['resource_type'];
      }

      this._filterDimensionByDisabledFields(this.dimensionDisabled);

      $cache.localSet(CUSTOM_TARGET_FIELDS, keys);
      this.field = keys;
      this.fieldColumns = columns;
      this.getList();
    },

    checkIsShow(bool) {
      return bool === undefined;
    },

    handleTableFilterConfirm(data) {
      this.filter[data.type] = data.str;
      this.getList();
    },

    handleTableFilterReset(type) {
      delete this.filter[type];
      this.getList();
    },

    // 获取默认自定列数据
    getDefaultFields() {
      const keys = [];
      const columns = defaultFieldsList.filter((field) => {
        if (field.type === 'target') {
          keys.push(field.key);
        }
        return field.type === 'target';
      });

      const arr = columns.reduce((prev, current) => {
        if (current.branches) {
          return prev.concat(current.branches);
        } else {
          prev.push(current);
          return prev;
        }
      }, []);

      return { keys, columns: arr };
    },

    // 获取默认维度数据
    getDefaultDimension() {
      const keys = [];
      const columns = defaultFieldsList.filter((field) => {
        if (field.type === 'dimension') {
          keys.push(field.key);
        }
        return field.type === 'dimension';
      });

      const arr = columns.reduce((prev, current) => {
        if (current.branches) {
          return prev.concat(current.branches);
        } else {
          prev.push(current);
          return prev;
        }
      }, []);

      return { keys, columns: arr };
    },

    // 获取本地自定义列数据
    getLocalTargets() {
      const keys = $cache.localGet(CUSTOM_TARGET_FIELDS);
      if (keys) {
        const columns = targetFieldsList.filter((field) =>
          keys.includes(field.key),
        );
        return { keys, columns };
      }
      return {};
    },

    // 获取本地维度数据
    getLocalDimension() {
      const keys = $cache.localGet(CUSTOM_DIMENSION_FIELDS);
      if (keys) {
        let columns = dimensionFieldsList.filter((field) =>
          keys.includes(field.key),
        );
        columns = columns.reduce((prev, current) => {
          if (current.branches) {
            return prev.concat(current.branches);
          } else {
            prev.push(current);
            return prev;
          }
        }, []);
        return { keys, columns };
      }
      return {};
    },

    // 格式化表格数据
    formatTableData(list) {
      return list.map((item) => {
        if (item.date) {
          item.date = moment(item.date).format('YYYY-MM-DD');
        }

        // if (item.is_ocpc !== undefined) {
        //   item._is_ocpc = item.is_ocpc;
        //   item.is_ocpc = isOCPCMapping[item.is_ocpc];
        // }

        // if (item.account_type !== undefined) {
        //   item._account_type = item.account_type;
        //   item.account_type = accountTypeMapping[item.account_type];
        // }

        return item;
      });
    },

    // 列表排序方法
    // 因为后端返回的数据为字符串
    // 所以需要转换成数字进行比较
    handleSortChange({ column, order, prop }) {
      if (order) {
        const ord = order === 'ascending' ? 'asc' : 'desc';
        this.order = `${prop}:${ord}`;
      } else {
        this.order = '';
      }
      this.getList();
    },

    handleSummaryMethod({ columns, data }) {
      const sums = [];

      columns.forEach((column, index) => {
        const value = this.dataSourceSum[column.property];
        if (index === 0) {
          sums.push('汇总');
          return;
        }

        if (value === 0 || value === undefined || value === '') {
          sums.push('-');
        } else {
          sums.push(value);
        }
      });

      return sums;
    },
  },
};
</script>

<style lang="less" scoped>
.title {
  margin-bottom: 20px;
}

.comp {
  margin-right: 20px;
  margin-bottom: 20px;
}

.tabs {
  margin-top: 20px;
}

.detail {
  &-actions {
    display: flex;
    justify-content: space-between;
    margin-bottom: 24px;
    margin-top: 40px;
    align-items: center;
  }
}

.table-filter {
  position: absolute;
  right: 2px;
  top: 50%;
  transform: translate(0, -50%);
}

.pagination {
  float: right;
  margin-top: 20px;
}
</style>
